import { FC, Fragment, useEffect, useState } from "react";
import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import {
  XMarkIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/outline";

interface BonzahAcknowledgementProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export const BONZAH_POLICY_LINKS = [
  {
    title: "Eligible & restricted vehicle types",
    short: "Eligible vehicles",
    subtitle: "Confirm the booked vehicle is covered by Bonzah",
    href: "https://bonzah.com/included-and-restricted-vehicle-types",
  },
  {
    title: "Bonzah Terms",
    short: "Terms",
    subtitle: "Terms governing the Bonzah insurance offering",
    href: "https://bonzah.com/terms",
  },
  {
    title: "Bonzah Privacy Policy",
    short: "Privacy Policy",
    subtitle: "How Bonzah collects and uses your data",
    href: "https://bonzah.com/privacy",
  },
];

const BonzahAcknowledgement: FC<BonzahAcknowledgementProps> = ({
  isOpen,
  onClose,
  onConfirm,
}) => {
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    if (!isOpen) setAgreed(false);
  }, [isOpen]);

  const handleConfirm = () => {
    if (!agreed) return;
    onConfirm();
  };

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/55" aria-hidden="true" />
        </TransitionChild>

        <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-4"
              enterTo="opacity-100 translate-y-0"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-4"
            >
              <DialogPanel className="w-full max-w-[560px] max-h-[calc(100vh-32px)] flex flex-col bg-bg-color border border-secondary-100 rounded-2xl shadow-2xl overflow-hidden">
                <div className="flex flex-shrink-0 items-start justify-between border-b border-secondary-100 px-6 pt-6 pb-4">
                  <div>
                    <h2 className="text-xl font-bold text-text-color">
                      Confirm Bonzah Insurance
                    </h2>
                    <p className="mt-1 text-sm text-text-color opacity-70">
                      Please review and acknowledge that the vehicle you are
                      renting is eligible for Bonzah coverage, you have read
                      and agree to Bonzah&apos;s Terms, and to Bonzah&apos;s
                      Privacy Policy before selecting Bonzah Insurance.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="text-text-color opacity-70 hover:opacity-100"
                    onClick={onClose}
                    aria-label="Close"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto">
                  <div className="mx-6 mt-4 rounded-lg border border-secondary-100 bg-secondary-50/40 px-4 py-3 text-xs leading-relaxed text-text-color opacity-90">
                    <p className="mb-1 text-sm font-semibold opacity-100">
                      Insurance Disclosure
                    </p>
                    <p>
                      By purchasing coverage through this site, you acknowledge
                      that Pablow Inc. dba{" "}
                      <a
                        href="https://bonzah.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:no-underline"
                      >
                        Bonzah.com
                      </a>{" "}
                      (&ldquo;Bonzah&rdquo;) is the licensed broker of record
                      and offers insurance coverage through various insurance
                      carriers. The specific carrier issuing your policy will
                      be identified at the time of purchase and in your policy
                      documents.
                    </p>
                    <p className="mt-2">
                      Coverage may include Collision Damage Waiver (CDW/LDW),
                      Rental Car Liability Insurance (RCLI), and Supplemental
                      Liability Insurance (SLI) as offered. Coverage excludes
                      medical payments, Personal Injury Protection (PIP),
                      Underinsured Motorist (UIM), and Uninsured Motorist (UM)
                      coverage where permitted by law. Full terms, conditions,
                      limits, and exclusions are set forth in the policy
                      documents provided at the time of purchase.
                    </p>
                    <p className="mt-2">
                      By proceeding with your purchase, you agree to
                      Bonzah.com&apos;s Terms of Service and Privacy Policy.
                    </p>
                  </div>

                  <div className="px-6 pt-4 pb-2 space-y-2">
                    {BONZAH_POLICY_LINKS.map((link) => (
                      <a
                        key={link.href}
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-start justify-between gap-3 rounded-lg border border-secondary-100 px-4 py-3 transition hover:bg-secondary-50"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-text-color">
                            {link.title}
                          </div>
                          <div className="mt-0.5 text-xs text-text-color opacity-70">
                            {link.subtitle}
                          </div>
                        </div>
                        <ArrowTopRightOnSquareIcon className="mt-0.5 h-5 w-5 shrink-0 text-text-color opacity-70" />
                      </a>
                    ))}
                  </div>

                  <label className="flex items-start gap-3 px-6 py-4 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={agreed}
                      onChange={(e) => setAgreed(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-secondary-100 text-primary-200 focus:ring-primary-200"
                    />
                    <span className="text-sm text-text-color">
                      I have reviewed the eligible vehicle types, Terms, and
                      Privacy Policy and confirm I agree.
                    </span>
                  </label>
                </div>

                <div className="flex flex-shrink-0 justify-end gap-3 border-t border-secondary-100 px-6 py-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-md border border-secondary-100 px-4 py-2 text-sm font-medium text-text-color hover:bg-secondary-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirm}
                    disabled={!agreed}
                    className="rounded-md bg-primary-200 px-4 py-2 text-sm font-medium text-white transition disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    I Agree & Continue
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default BonzahAcknowledgement;
